import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProfileLink from '../components/ProfileLink';
import { useGame } from '../contexts/GameContext';
import { getStatisticsData, getStatisticsRankings, getTournamentList } from '../api/readApi';
import GoogleAd from '../components/GoogleAd';
import './Leaderboard.css';

const ALL_MATCHES_ID = '__all__';

export default function Leaderboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { gameId } = useGame();
  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState(() => searchParams.get('tournamentId') || '');
  const [rankingsSingles, setRankingsSingles] = useState([]);
  const [rankingsDoubles, setRankingsDoubles] = useState([]);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingTournaments(true);
    getTournamentList(gameId)
      .then((data) => {
        if (cancelled) return;
        setTournaments(data?.tournaments ?? []);
      })
      .catch(() => {
        if (!cancelled) setTournaments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTournaments(false);
      });
    return () => { cancelled = true; };
  }, [gameId]);

  useEffect(() => {
    const fromUrl = searchParams.get('tournamentId') || '';
    if (fromUrl !== tournamentId) setTournamentId(fromUrl);
  }, [searchParams, tournamentId]);

  useEffect(() => {
    if (!tournamentId) {
      setRankingsSingles([]);
      setRankingsDoubles([]);
      return;
    }
    let cancelled = false;
    setLoadingStats(true);
    const fetchRankings = tournamentId === ALL_MATCHES_ID
      ? getStatisticsRankings(gameId)
      : getStatisticsData(gameId, { tournamentId, part: 'rankings' });
    fetchRankings
      .then((data) => {
        if (cancelled) return;
        setRankingsSingles(data?.rankingsSingles ?? []);
        setRankingsDoubles(data?.rankingsDoubles ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setRankingsSingles([]);
          setRankingsDoubles([]);
          console.error('Error fetching leaderboard:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStats(false);
      });
    return () => { cancelled = true; };
  }, [gameId, tournamentId]);

  const handleTournamentChange = (e) => {
    const id = e.target.value || '';
    setTournamentId(id);
    if (id) {
      setSearchParams({ tournamentId: id }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const selectedTournament = tournamentId === ALL_MATCHES_ID ? { name: 'All matches' } : tournaments.find((t) => t.id === tournamentId);
  const loading = loadingTournaments || (tournamentId && loadingStats);
  const scopeLabel = tournamentId === ALL_MATCHES_ID ? 'All matches (historical)' : (selectedTournament?.name || 'Tournament');

  return (
    <div className="leaderboard-container leaderboard-page">
      <div className="leaderboard-header-row">
        <div className="leaderboard-heading">
          <h1 className="page-title">Leaderboard</h1>
          <p className="page-subtitle">Select a tournament or “All matches” for historical data – singles and doubles leaderboards by game type</p>
        </div>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary btn-back">
          Back to Dashboard
        </button>
      </div>

      <GoogleAd slotKey="BANNER" className="google-ad-banner" />

      <div className="leaderboard-select-wrap card-ui">
        <label htmlFor="leaderboard-tournament" className="leaderboard-select-label">Tournament</label>
        <select
          id="leaderboard-tournament"
          className="leaderboard-select"
          value={tournamentId}
          onChange={handleTournamentChange}
          disabled={loadingTournaments}
        >
          <option value="">Select a tournament…</option>
          <option value={ALL_MATCHES_ID}>All matches (historical)</option>
          {tournaments.map((t) => (
            <option key={t.id} value={t.id}>{t.name || t.id}</option>
          ))}
        </select>
      </div>

      {!tournamentId && !loading && (
        <div className="leaderboard-content card-ui leaderboard-card leaderboard-empty-state">
          <p className="no-data">Select a tournament above to view its leaderboard.</p>
        </div>
      )}

      {tournamentId && (
        <>
          {loading ? (
            <div className="leaderboard-content card-ui leaderboard-card">
              <div className="no-data">Loading leaderboard…</div>
            </div>
          ) : (
            <>
              {/* Singles leaderboard */}
              <div className="leaderboard-section-block">
                <div className="leaderboard-content card-ui leaderboard-card">
                  <h2 className="leaderboard-section-title leaderboard-section-singles" id="leaderboard-singles">
                    <span className="leaderboard-section-badge" aria-hidden>Singles</span>
                    <span className="leaderboard-section-scope">{scopeLabel}</span>
                  </h2>
                  {rankingsSingles.length === 0 ? (
                    <div className="no-data">
                      <p>No singles results yet.</p>
                    </div>
                  ) : (
                    <div className="leaderboard-table-wrap">
                      <div className="leaderboard-table">
                        <div className="table-header">
                          <div className="col-rank">Rank</div>
                          <div className="col-name">Player</div>
                          <div className="col-points">Points</div>
                          <div className="col-wins">Wins</div>
                          <div className="col-losses">Losses</div>
                          <div className="col-rate">Win Rate</div>
                        </div>
                        {rankingsSingles.slice(0, 100).map((player, index) => (
                        <div
                          key={player.playerId}
                          className={`table-row ${index < 3 ? `top-three rank-row-${index + 1}` : ''}`}
                        >
                          <div className="col-rank">
                            <span className={`rank-badge ${index < 3 ? `rank-medal rank-${index + 1}` : ''}`}>
                              {index === 0 && '🥇'}
                              {index === 1 && '🥈'}
                              {index === 2 && '🥉'}
                              {index >= 3 && (index + 1)}
                            </span>
                          </div>
                          <div className="col-name">
                            <ProfileLink userId={player.playerId} className="player-name-cell player-name-link">
                              {player.playerName}
                            </ProfileLink>
                          </div>
                          <div className="col-points">{player.ptsFor ?? 0}</div>
                          <div className="col-wins">{player.won ?? 0}</div>
                          <div className="col-losses">{player.lost ?? 0}</div>
                          <div className="col-rate">{player.winRate ?? '0'}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              </div>

              {/* Doubles leaderboard */}
              <div className="leaderboard-section-block">
                <div className="leaderboard-content card-ui leaderboard-card">
                  <h2 className="leaderboard-section-title leaderboard-section-doubles" id="leaderboard-doubles">
                    <span className="leaderboard-section-badge" aria-hidden>Doubles</span>
                    <span className="leaderboard-section-scope">{scopeLabel}</span>
                  </h2>
                  {rankingsDoubles.length === 0 ? (
                    <div className="no-data">
                      <p>No doubles results yet.</p>
                    </div>
                  ) : (
                    <div className="leaderboard-table-wrap">
                      <div className="leaderboard-table">
                        <div className="table-header">
                          <div className="col-rank">Rank</div>
                          <div className="col-name">Player / Team</div>
                          <div className="col-points">Points</div>
                          <div className="col-wins">Wins</div>
                          <div className="col-losses">Losses</div>
                          <div className="col-rate">Win Rate</div>
                        </div>
                        {rankingsDoubles.slice(0, 100).map((row, index) => (
                        <div
                          key={row.pairKey || row.playerId}
                          className={`table-row ${index < 3 ? `top-three rank-row-${index + 1}` : ''}`}
                        >
                          <div className="col-rank">
                            <span className={`rank-badge ${index < 3 ? `rank-medal rank-${index + 1}` : ''}`}>
                              {index === 0 && '🥇'}
                              {index === 1 && '🥈'}
                              {index === 2 && '🥉'}
                              {index >= 3 && (index + 1)}
                            </span>
                          </div>
                          <div className="col-name">
                            <span className="player-name-cell">
                              {row.playerIds?.length === 2 && row.playerNames?.length === 2 ? (
                                <>
                                  <ProfileLink userId={row.playerIds[0]} className="player-name-link">{row.playerNames[0]}</ProfileLink>
                                  <span className="doubles-sep"> & </span>
                                  <ProfileLink userId={row.playerIds[1]} className="player-name-link">{row.playerNames[1]}</ProfileLink>
                                </>
                              ) : (
                                <ProfileLink userId={row.playerId} className="player-name-link">{row.playerName}</ProfileLink>
                              )}
                              {row.teamName && <span className="doubles-team-name" title="Team name"> ({row.teamName})</span>}
                            </span>
                          </div>
                          <div className="col-points">{row.ptsFor ?? 0}</div>
                          <div className="col-wins">{row.won ?? 0}</div>
                          <div className="col-losses">{row.lost ?? 0}</div>
                          <div className="col-rate">{row.winRate ?? '0'}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
